import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormGrid, SectionTitle, SelectField, CreatableSelectField, TagsField, TextAreaField, TextField } from "./Fields";

const empty = {
  title: "", client_id: null, hiring_manager_id: null, department: "Engineering", location: "",
  work_mode: "On-site", employment_type: "Full Time", min_experience: null, max_experience: null,
  min_ctc: null, max_ctc: null, openings: 1, priority: "Medium", required_skills: [],
  preferred_skills: [], education: "", industry: "Automotive", notice_period: "30 days",
  description: "", responsibilities: "", requirements: "", recruiter_id: null, sourcer_id: null,
  custom_note: "",
  opening_date: new Date().toISOString().slice(0, 10), closing_date: "", sla_days: 21, status: "Open",
};

export default function JobForm({ open, onOpenChange, job, onSaved }) {
  const { meta, refreshMeta } = useAuth();
  const [form, setForm] = useState(empty);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const industries = Object.keys(meta?.skill_taxonomy || {});

  useEffect(() => {
    if (!open) return;
    setForm(job ? { ...empty, ...job } : empty);
  }, [open, job]);

  useEffect(() => {
    if (!form.client_id) return setContacts([]);
    api.get(`/clients/${form.client_id}/contacts`).then((r) => setContacts(r.data)).catch(() => setContacts([]));
  }, [form.client_id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const skillPool = [
    ...(meta?.skill_taxonomy?.[form.industry] || []),
    ...(meta?.skill_taxonomy?.Software || []),
  ];

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Job title is required");
    if (!form.client_id) return toast.error("Please select a client");
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      ["client_name", "recruiter_name", "sourcer_name", "stage_counts", "funnel", "applicant_count",
        "notes", "activity", "created_at", "updated_at", "created_by", "job_code", "client_industry",
        "hiring_manager_name"].forEach((k) => delete payload[k]);
      const { data } = job
        ? await api.put(`/jobs/${job.id}`, payload)
        : await api.post("/jobs", payload);
      toast.success(job ? "Job updated" : `Job created · ${data.job_code}`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle className="text-lg font-medium text-slate-900">
            {job ? `Edit ${job.job_code}` : "Create a new job"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Requirements drive the AI match score, so keep skills and experience accurate.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(92vh-180px)]">
          <div className="space-y-5 px-6 py-5">
            <SectionTitle>Role</SectionTitle>
            <FormGrid>
              <TextField label="Job title" value={form.title} onChange={set("title")} required testid="job-title-input" placeholder="Senior Embedded Engineer" />
              <SelectField label="Client" value={form.client_id} onChange={set("client_id")} testid="job-client-select"
                options={(meta?.clients || []).map((c) => ({ value: c.id, label: c.name }))} />
              <SelectField label="Hiring manager" value={form.hiring_manager_id} onChange={set("hiring_manager_id")} testid="job-hm-select"
                options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.designation ? ` · ${c.designation}` : ""}` }))} />
              <TextField label="Department" value={form.department} onChange={set("department")} />
              <TextField label="Location" value={form.location} onChange={set("location")} testid="job-location-input" placeholder="Bangalore, Karnataka" />
              <SelectField label="Work mode" value={form.work_mode} onChange={set("work_mode")} options={meta?.enums?.work_modes || []} />
              <SelectField label="Employment type" value={form.employment_type} onChange={set("employment_type")} options={meta?.enums?.employment_types || []} />
              <CreatableSelectField
                label="Industry"
                value={form.industry}
                onChange={set("industry")}
                options={industries}
                addLabel="Add new industry"
                testid="job-industry-select"
                onCreate={async (name) => {
                  try {
                    await api.post("/settings/skills", { category: name, skills: [] });
                    await refreshMeta?.();
                    toast.success(`${name} added as an industry`);
                  } catch (e) {
                    toast.error(apiError(e, `Could not add ${name} as an industry`));
                    return false;
                  }
                }}
              />
            </FormGrid>

            <SectionTitle>Scope &amp; compensation</SectionTitle>
            <FormGrid cols={3}>
              <TextField label="Min experience (yrs)" type="number" value={form.min_experience} onChange={set("min_experience")} testid="job-min-exp" />
              <TextField label="Max experience (yrs)" type="number" value={form.max_experience} onChange={set("max_experience")} />
              <TextField label="Openings" type="number" value={form.openings} onChange={set("openings")} />
              <TextField label="Min CTC (LPA)" type="number" value={form.min_ctc} onChange={set("min_ctc")} />
              <TextField label="Max CTC (LPA)" type="number" value={form.max_ctc} onChange={set("max_ctc")} testid="job-max-ctc" />
              <SelectField label="Priority" value={form.priority} onChange={set("priority")} options={meta?.enums?.priorities || []} testid="job-priority-select" />
            </FormGrid>

            <SectionTitle>Skills &amp; education</SectionTitle>
            <TagsField label="Required skills" values={form.required_skills} onChange={set("required_skills")} suggestions={skillPool} testid="job-required-skills" />
            <TagsField label="Preferred skills" values={form.preferred_skills} onChange={set("preferred_skills")} suggestions={skillPool} />
            <FormGrid>
              <TextField label="Education" value={form.education} onChange={set("education")} placeholder="B.E./B.Tech in Electronics" />
              <TextField label="Notice period accepted" value={form.notice_period} onChange={set("notice_period")} placeholder="30 days" />
            </FormGrid>

            <SectionTitle>Description</SectionTitle>
            <TextAreaField label="Job description" value={form.description} onChange={set("description")} testid="job-description" />
            <TextAreaField label="Responsibilities" value={form.responsibilities} onChange={set("responsibilities")} />
            <TextAreaField label="Requirements" value={form.requirements} onChange={set("requirements")} />

            <SectionTitle>Custom note</SectionTitle>
            <TextAreaField
              label="Internal note for this requirement"
              value={form.custom_note}
              onChange={set("custom_note")}
              rows={4}
              testid="job-custom-note"
              placeholder="Anything your recruiters should know — screening must-haves, client quirks, interview panel preferences, budget flexibility…"
            />
            <p className="-mt-2 text-[11px] text-slate-400">
              Visible to your internal team on the job page. Never shown to client or candidate portal users.
            </p>

            <SectionTitle>Ownership &amp; timeline</SectionTitle>
            <FormGrid cols={3}>
              <SelectField label="Recruiter" value={form.recruiter_id} onChange={set("recruiter_id")} testid="job-recruiter-select"
                options={(meta?.users || []).filter((u) => ["recruiter", "recruitment_manager", "admin", "super_admin"].includes(u.role)).map((u) => ({ value: u.id, label: u.name }))} />
              <SelectField label="Sourcer" value={form.sourcer_id} onChange={set("sourcer_id")}
                options={(meta?.users || []).filter((u) => ["sourcer", "recruiter"].includes(u.role)).map((u) => ({ value: u.id, label: u.name }))} />
              <SelectField label="Status" value={form.status} onChange={set("status")} options={meta?.enums?.job_statuses || []} testid="job-status-select" />
              <TextField label="Opening date" type="date" value={form.opening_date} onChange={set("opening_date")} />
              <TextField label="Closing date" type="date" value={form.closing_date} onChange={set("closing_date")} />
              <TextField label="SLA (days)" type="number" value={form.sla_days} onChange={set("sla_days")} />
            </FormGrid>
          </div>
        </ScrollArea>
        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="job-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {job ? "Save changes" : "Create job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
